/**
 * Calculate the number of nights between two dates.
 *
 * @param {string | Date} startDate - The date the guest checks into the hotel.
 * @param {string | Date} endDate - The date the guest checks out of the hotel.
 *
 * @returns {number} The total number of nights stayed.
 *
 * @example
 * DateDuration("2025-08-01", "2025-08-05"); // 4
 */
export const dateDuration = (startDate: string, endDate: string): number => {
  const diffInTime: number = new Date(endDate).getTime() - new Date(startDate).getTime();
  const duration = diffInTime / (1000 * 60 * 60 * 24);
  return Math.max(1, Math.ceil(duration));
  // return Math.ceil(duration);
};


/**
 * Returns the start and end Date objects representing the full range of the current day.
 * 
 * Useful for filtering records (e.g., bookings, bills, logs) that occurred within today.
 *
 * @returns {{ start: Date, end: Date }} An object containing:
 * - `start`: The beginning of the day (00:00:00.000)
 * - `end`: The end of the day (23:59:59.999)
 */
export function getTodayRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { start, end };
}


/**
 * Returns the start and end Date objects for a given date string.
 *
 * @param {string} dateStr - The date in "YYYY-MM-DD" format
 * @returns {{ start: Date, end: Date }} The full range of that day
 */
export function getRangeForDate(dateStr: string) {
  const [year, month, day] = dateStr.split("-").map(Number);
  const start = new Date(year, month - 1, day, 0, 0, 0, 0);
  const end = new Date(year, month - 1, day, 23, 59, 59, 999);
  return { start, end };
}


/**
 * Returns a new Date object set to midnight (00:00:00) for comparison
 */
export function normalizeDate(date: Date | string): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}


export const todayDate = (): string => {
  return new Date().toISOString();
}


/**
 * formatDateTime
 * -----------------
 * Converts a date string or Date object into a human-readable format
 * including both the date and time in 12-hour format.
 *
 * @param dateString - The input date, either as a string or a Date object.
 * @returns A formatted string in the format: "Sep 24, 2025, 2:30 PM"
 *
 * @example
 * formatDateTime("2025-09-24T14:30:00Z");
 * // Returns: "Sep 24, 2025, 2:30 PM"
 *
 * formatDateTime(new Date());
 * // Returns current date and time in the same format
 */
export const formatDateTime = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};


export const formatDate = (dateString: string | Date): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString([], {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};


export const formatTime = (dateString: string | Date): string => {
  return new Date(dateString).toLocaleString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  })
}


export function attachCurrentTime(dateString: string) {
  const now = new Date();
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());
  return date.toISOString();
}


// Convert Date object to yyyy-MM-dd
export function formatDateForInput(date: Date | string) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


export const getBookingBusinessDate = () => {
    const now = new Date();
    const hour = now.getHours();

    if (hour >= 0 && hour < 5) {
        now.setDate(now.getDate() - 1);
    }

    // Return YYYY-MM-DD (local date)
    return now.toLocaleDateString('en-CA');
};
